// アクション: コマンド列の記録と再生 (Photoshop のアクションパネル相当)。
// UI のメニュー / ダイアログはコマンド層 (commands/registry) を経由するので、
// 記録中に成功したコマンドをそのまま並べれば再生できる。localStorage に保存する。
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { runCommand, onCommandRun } from '@/commands/registry'
import { toast } from '@/composables/useToast'
import { t } from '@/i18n'
import { nanoid } from 'nanoid'

export interface ActionStep {
  command: string
  params: Record<string, unknown>
}
export interface ActionDef {
  id: string
  name: string
  steps: ActionStep[]
}

const STORAGE_KEY = 'bpe.actions.v1'
/** 記録しないコマンド (状態を変えない、または再生に意味がない) */
// file.open / layer.placeImage / file.export は base64 が巨大なので記録しない
const EXCLUDED = new Set(['document.describe', 'document.preview', 'view.fit', 'view.zoom', 'edit.undo', 'edit.redo', 'document.saveProject', 'file.open', 'file.export', 'layer.placeImage', 'font.list', 'font.loadLocal', 'font.loadFile'])

function load(): ActionDef[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    return sanitize(parsed)
  } catch {
    return []
  }
}

/** 外部 JSON (読み込み / 保存データ) を検証して ActionDef[] にする。不正なら例外 */
export function sanitize(input: unknown): ActionDef[] {
  const arr = Array.isArray(input) ? input : (input as { actions?: unknown })?.actions
  if (!Array.isArray(arr)) throw new Error('アクションの配列ではない')
  const out: ActionDef[] = []
  for (const a of arr) {
    if (!a || typeof a !== 'object') throw new Error('アクションが不正')
    const o = a as Record<string, unknown>
    if (typeof o.name !== 'string' || !Array.isArray(o.steps)) throw new Error('アクションに name / steps が無い')
    const steps: ActionStep[] = []
    for (const st of o.steps) {
      const s = st as Record<string, unknown>
      if (!s || typeof s.command !== 'string') throw new Error('ステップが不正')
      const params = s.params && typeof s.params === 'object' && !Array.isArray(s.params) ? (s.params as Record<string, unknown>) : {}
      steps.push({ command: s.command, params: JSON.parse(JSON.stringify(params)) })
    }
    out.push({ id: typeof o.id === 'string' && o.id ? o.id : nanoid(8), name: o.name.slice(0, 200), steps })
  }
  return out
}

export const useActionsStore = defineStore('actions', () => {
  const actions = ref<ActionDef[]>(load())
  const recording = ref<{ name: string; steps: ActionStep[] } | null>(null)
  const playing = ref(false)

  watch(
    actions,
    v => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(v))
      } catch {
        /* 容量超過などは無視 */
      }
    },
    { deep: true }
  )

  function startRecording(name?: string): void {
    recording.value = { name: name ?? `${t('actions.newName')} ${actions.value.length + 1}`, steps: [] }
  }
  function stopRecording(): ActionDef | null {
    const r = recording.value
    recording.value = null
    if (!r) return null
    const def: ActionDef = { id: nanoid(8), name: r.name, steps: r.steps }
    actions.value.push(def)
    return def
  }
  function cancelRecording(): void {
    recording.value = null
  }
  function record(command: string, params: Record<string, unknown>): void {
    if (!recording.value || playing.value || EXCLUDED.has(command)) return
    recording.value.steps.push({ command, params: JSON.parse(JSON.stringify(params)) })
  }

  /** 順に実行。失敗したら止めて例外 */
  async function play(id: string): Promise<void> {
    const a = actions.value.find(x => x.id === id)
    if (!a || playing.value) return
    playing.value = true
    try {
      for (const st of a.steps) await runCommand(st.command, st.params)
      toast.success(t('actions.played', { name: a.name }))
    } catch (e) {
      toast.warn(t('actions.failed', { error: e instanceof Error ? e.message : String(e) }))
      throw e
    } finally {
      playing.value = false
    }
  }
  function remove(id: string): void {
    actions.value = actions.value.filter(a => a.id !== id)
  }
  function rename(id: string, name: string): void {
    const a = actions.value.find(x => x.id === id)
    if (a) a.name = name
  }
  function removeStep(id: string, index: number): void {
    const a = actions.value.find(x => x.id === id)
    if (a) a.steps.splice(index, 1)
  }
  function exportJson(ids?: string[]): string {
    const list = ids ? actions.value.filter(a => ids.includes(a.id)) : actions.value
    return JSON.stringify({ format: 'browser-photo-editor-actions', version: 1, actions: list }, null, 2)
  }
  function importJson(text: string): number {
    const list = sanitize(JSON.parse(text))
    for (const a of list) {
      // 同じ id があれば置き換える
      const i = actions.value.findIndex(x => x.id === a.id)
      if (i >= 0) actions.value[i] = a
      else actions.value.push(a)
    }
    return list.length
  }

  return { actions, recording, playing, startRecording, stopRecording, cancelRecording, record, play, remove, rename, removeStep, exportJson, importJson }
})

let installed = false
/** コマンド成功時にアクション記録へ流す。main.ts で 1 回呼ぶ */
export function installActionRecorder(): void {
  if (installed) return
  installed = true
  onCommandRun((id, params) => useActionsStore().record(id, params))
}
